import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, fetchImageObjectUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActivityPicker, AvailabilityEditor } from "@/components/member";
import { IdentityVerification } from "@/components/IdentityVerification";
import SafetyExclusions from "@/components/SafetyExclusions";
import { PageLoader } from "@/components/common";
import PayPalSubscribeButton from "@/components/PayPalSubscribeButton";
import MembershipPlans from "@/components/MembershipPlans";
import { COMMUNITIES } from "@/pages/member/Onboarding";
import { getSubscriptionState, enablePush, disablePush } from "@/lib/push";
import { ShieldCheck, ShieldAlert, LogOut, ExternalLink, Check, Trash2, AlertTriangle, Bell } from "lucide-react";

const RADII = [5, 10, 15, 25, 50];
const STATUS_LABEL = { active: "Active", inactive: "Inactive", canceled: "Canceled", past_due: "Past Due", suspended: "Suspended", trial: "Trial" };

export default function Profile() {
    const { user, logout, refresh } = useAuth();
    const [params] = useSearchParams();
    const [tab, setTab] = useState(params.get("tab") || "preferences");
    const [acts, setActs] = useState(null);
    const [prefs, setPrefs] = useState({});
    const [avail, setAvail] = useState([]);
    const [membership, setMembership] = useState(null);
    const [community, setCommunity] = useState(user?.city && COMMUNITIES[user.city] ? user.city : "Tampa");
    const [radius, setRadius] = useState(user?.travel_radius || 25);

    const load = () => {
        api.get("/activities").then((r) => setActs(r.data));
        api.get("/me/full").then((r) => {
            const p = {}; r.data.preferences.forEach((x) => (p[x.activity_id] = x.level));
            setPrefs(p); setAvail(r.data.availability);
            setRadius(r.data.user.travel_radius || 25);
        });
        api.get("/me/membership").then((r) => setMembership(r.data));
    };
    useEffect(load, []);
    const [newsletter, setNewsletter] = useState(false);
    useEffect(() => { api.get("/me/full").then((r) => setNewsletter(!!r.data.user?.newsletter_opt_in)).catch(() => {}); }, []);
    const toggleNews = async (v) => {
        setNewsletter(v);
        try { await api.put("/me/newsletter", { opt_in: v }); toast.success(v ? "Subscribed to the monthly newsletter." : "Unsubscribed."); }
        catch (e) { setNewsletter(!v); toast.error("Could not update your preference."); }
    };
    const [pushState, setPushState] = useState("loading");
    const [pushBusy, setPushBusy] = useState(false);
    useEffect(() => { getSubscriptionState().then(setPushState).catch(() => setPushState("unsupported")); }, []);
    const togglePush = async (v) => {
        setPushBusy(true);
        try {
            if (v) { await enablePush(); setPushState("subscribed"); toast.success("Push notifications enabled on this device."); }
            else { await disablePush(); setPushState("unsubscribed"); toast.success("Push notifications turned off."); }
        } catch (e) {
            toast.error(e.message || "Could not update push notifications.");
            setPushState(await getSubscriptionState().catch(() => "unsupported"));
        } finally { setPushBusy(false); }
    };
    const [delOpen, setDelOpen] = useState(false);
    const [delConfirm, setDelConfirm] = useState(false);
    const [delWord, setDelWord] = useState("");
    const [deleting, setDeleting] = useState(false);
    const canDelete = delConfirm && delWord.trim().toLowerCase() === "delete";
    const deleteAccount = async () => {
        if (!canDelete) return;
        setDeleting(true);
        try {
            await api.delete("/me/account");
            toast.success("Your account and all your data have been permanently deleted.");
            logout();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setDeleting(false); }
    };
    const [photoUrl, setPhotoUrl] = useState(null);
    useEffect(() => {
        const load = () => fetchImageObjectUrl("/me/photo").then(setPhotoUrl);
        load();
        window.addEventListener("ssc-photo-updated", load);
        return () => window.removeEventListener("ssc-photo-updated", load);
    }, []);
    if (!acts || !membership) return <PageLoader />;

    const togglePref = (a) => setPrefs((p) => { const n = { ...p }; if (!n[a.id]) n[a.id] = "love"; else if (n[a.id] === "love") n[a.id] = "try"; else delete n[a.id]; return n; });
    const savePrefs = async () => {
        const preferences = Object.entries(prefs).map(([activity_id, level]) => ({ activity_id, level }));
        await api.put("/me/preferences", { preferences });
        toast.success("Interests saved.");
    };
    const addAvail = async (w) => { const { data } = await api.post("/me/availability", w); setAvail((a) => [...a, data]); };
    const updAvail = async (id, w) => { const { data } = await api.put(`/me/availability/${id}`, w); setAvail((a) => a.map((x) => (x.id === id ? data : x))); };
    const delAvail = async (w) => { await api.delete(`/me/availability/${w.id}`); setAvail((a) => a.filter((x) => x.id !== w.id)); };
    const saveAccount = async () => {
        const [lat, lon] = COMMUNITIES[community];
        await api.put("/me/profile", { city: community, lat, lon, travel_radius: radius });
        await refresh();
        toast.success("Profile updated.");
    };
    const onPaid = async (subId, tier) => { await api.post("/me/membership/confirm", { provider_subscription_id: subId, tier }); const r = await api.get("/me/membership"); setMembership(r.data); toast.success("Membership active."); };
    const cancelSub = async () => { await api.post("/me/membership/cancel"); const r = await api.get("/me/membership"); setMembership(r.data); toast.success("Membership canceled."); };
    const reactivate = async () => { await api.post("/me/membership/reactivate"); const r = await api.get("/me/membership"); setMembership(r.data); toast.success("Membership reactivated."); };

    const mStatus = membership.membership?.status || "inactive";

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setTab("identity")} data-testid="profile-avatar" className="h-14 w-14 shrink-0 overflow-hidden rounded-full border border-border bg-secondary/60">
                        {photoUrl ? <img src={photoUrl} alt="You" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-lg font-700 text-muted-foreground">{(user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")}</span>}
                    </button>
                    <div>
                    <h1 className="font-display text-2xl font-700 tracking-tight text-primary">{user?.first_name} {user?.last_name}</h1>
                    <div className="mt-1 flex items-center gap-2 text-sm">
                        {user?.verification_status === "verified" ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-600 text-primary"><ShieldCheck className="h-3.5 w-3.5" /> Verified Member</span>
                        ) : user?.verification_status === "needs_review" ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-600 text-amber-700"><ShieldAlert className="h-3.5 w-3.5" /> Verification Under Review</span>
                        ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-600 text-amber-700"><ShieldAlert className="h-3.5 w-3.5" /> Pending Verification</span>
                        )}
                    </div>
                </div>
                </div>
                <Button onClick={logout} variant="ghost" size="sm" data-testid="logout-btn" className="text-muted-foreground"><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="flex w-full overflow-x-auto">
                    <TabsTrigger value="preferences" data-testid="tab-preferences" className="flex-1 whitespace-nowrap text-xs sm:text-sm">Interests</TabsTrigger>
                    <TabsTrigger value="availability" data-testid="tab-availability" className="flex-1 whitespace-nowrap text-xs sm:text-sm">Availability</TabsTrigger>
                    <TabsTrigger value="identity" data-testid="tab-identity" className="flex-1 whitespace-nowrap text-xs sm:text-sm">Verify ID</TabsTrigger>
                    <TabsTrigger value="membership" data-testid="tab-membership" className="flex-1 whitespace-nowrap text-xs sm:text-sm">Membership</TabsTrigger>
                    <TabsTrigger value="account" data-testid="tab-account" className="flex-1 whitespace-nowrap text-xs sm:text-sm">Account</TabsTrigger>
                </TabsList>

                <TabsContent value="preferences" className="mt-4">
                    <ActivityPicker byCategory={acts.by_category} categories={acts.categories} mode="prefs" selected={prefs} onToggle={togglePref} />
                    <Button onClick={savePrefs} data-testid="save-prefs" className="mt-6 rounded-full bg-primary hover:bg-primary/90">Save interests</Button>
                </TabsContent>

                <TabsContent value="availability" className="mt-4">
                    <AvailabilityEditor items={avail} onAdd={addAvail} onUpdate={updAvail} onDelete={delAvail} testidPrefix="profile-avail" />
                </TabsContent>

                <TabsContent value="identity" className="mt-4">
                    <IdentityVerification onStatusChange={refresh} />
                </TabsContent>

                <TabsContent value="membership" className="mt-4">
                    <div className="overflow-hidden rounded-2xl bg-card shadow-sm">
                        <div className="p-6">
                            <p className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Current status</p>
                            <div className="mt-1 flex flex-wrap items-baseline gap-2">
                                <p className="font-display text-2xl font-700 text-primary">{STATUS_LABEL[mStatus] || mStatus}</p>
                                {mStatus === "active" && <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-600 text-primary">Membership active</span>}
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground">
                                {membership.tier === "unlimited" ? "$50" : "$20"}/month membership. Activity costs (food, admission, parking, etc.) are separate.
                            </p>
                            {mStatus === "active" && (
                                <p className="mt-1 text-sm text-muted-foreground" data-testid="membership-usage">
                                    Plan: <span className="font-600 capitalize text-foreground">{membership.tier}</span>
                                    {membership.outing_cap
                                        ? ` · ${membership.outings_used} of ${membership.outing_cap} outings used this month`
                                        : " · Unlimited outings"}
                                </p>
                            )}
                        </div>

                        {mStatus !== "active" ? (
                            <div className="border-t border-border bg-secondary/40 p-6" data-testid="profile-subscribe">
                                <p className="font-600 text-primary">Start your membership</p>
                                <p className="mt-1 text-sm text-muted-foreground">Pick a plan and subscribe securely with PayPal — cancel anytime.</p>
                                <div className="mt-4"><MembershipPlans onPaid={onPaid} /></div>
                                {mStatus === "canceled" && (
                                    <Button onClick={reactivate} variant="outline" data-testid="profile-reactivate" className="mt-3 w-full max-w-sm rounded-full">Reactivate previous plan</Button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-5 border-t border-border p-6">
                                {membership.tier === "standard" && (
                                    <div data-testid="profile-upgrade">
                                        <p className="font-600 text-primary">Want more outings?</p>
                                        <p className="mt-1 text-sm text-muted-foreground">Upgrade to Unlimited for unlimited outings every month.</p>
                                        <div className="mt-3 max-w-sm"><MembershipPlans onPaid={onPaid} only="unlimited" /></div>
                                    </div>
                                )}
                                <Button onClick={cancelSub} variant="outline" data-testid="profile-cancel-sub" className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10">Cancel subscription</Button>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="account" className="mt-4 space-y-4">
                    <div className="rounded-2xl bg-card p-6 shadow-sm">
                        <p className="text-sm text-muted-foreground">Email</p><p className="font-600 text-primary">{user?.email}</p>
                        <p className="mt-3 text-sm text-muted-foreground">Phone</p><p className="font-600 text-primary">{user?.phone || "—"}</p>
                        <p className="mt-1 text-xs text-muted-foreground">Your contact details and date of birth are private and never shown to other members.</p>
                    </div>
                    <div className="rounded-2xl bg-card p-6 shadow-sm">
                        <label className="text-sm font-600 text-primary">Your area</label>
                        <Select value={community} onValueChange={setCommunity}>
                            <SelectTrigger data-testid="account-community" className="mt-2 max-w-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.keys(COMMUNITIES).map((c) => <SelectItem key={c} value={c}>{c}, FL</SelectItem>)}</SelectContent>
                        </Select>
                        <label className="mt-5 block text-sm font-600 text-primary">Travel distance</label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {RADII.map((r) => (
                                <button key={r} data-testid={`account-radius-${r}`} onClick={() => setRadius(r)} className={`rounded-full border px-4 py-2 text-sm font-600 ${radius === r ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{r} mi</button>
                            ))}
                        </div>
                        <Button onClick={saveAccount} data-testid="save-account" className="mt-6 rounded-full bg-primary hover:bg-primary/90"><Check className="mr-1 h-4 w-4" /> Save</Button>
                    </div>

                    <SafetyExclusions />

                    <div className="flex items-start justify-between gap-4 rounded-2xl bg-card p-6 shadow-sm" data-testid="push-optin">
                        <div>
                            <p className="flex items-center gap-2 font-600 text-primary"><Bell className="h-4 w-4" /> Push notifications</p>
                            <p className="mt-0.5 text-sm text-muted-foreground">
                                {pushState === "unsupported"
                                    ? "This device or browser doesn't support push notifications. Install the app to your home screen to enable them."
                                    : pushState === "denied"
                                        ? "Notifications are blocked in your browser settings. Allow them for this site to turn on push."
                                        : "Get club announcements and outing updates on this device, even when the app is closed."}
                            </p>
                        </div>
                        <Switch
                            data-testid="push-switch"
                            disabled={pushBusy || pushState === "unsupported" || pushState === "denied" || pushState === "loading"}
                            checked={pushState === "subscribed"}
                            onCheckedChange={togglePush}
                        />
                    </div>

                    <div className="flex items-start justify-between gap-4 rounded-2xl bg-card p-6 shadow-sm" data-testid="newsletter-optin">
                        <div>
                            <p className="font-600 text-primary">Monthly newsletter</p>
                            <p className="mt-0.5 text-sm text-muted-foreground">Get community updates, new activities, and announcements once a month. Unsubscribe anytime.</p>
                        </div>
                        <Switch data-testid="newsletter-switch" checked={newsletter} onCheckedChange={toggleNews} />
                    </div>

                    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6" data-testid="danger-zone">
                        <p className="font-600 text-destructive">Delete account</p>
                        <p className="mt-1 text-sm text-muted-foreground">Permanently remove your profile, membership, and all activity from Single Social Club. This cannot be undone.</p>
                        <Button onClick={() => { setDelConfirm(false); setDelWord(""); setDelOpen(true); }} data-testid="delete-account-open" variant="outline" className="mt-4 rounded-full border-destructive/40 text-destructive hover:bg-destructive/10"><Trash2 className="mr-1 h-4 w-4" /> Delete my account</Button>
                    </div>

                    <Dialog open={delOpen} onOpenChange={setDelOpen}>
                        <DialogContent data-testid="delete-account-dialog">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="h-5 w-5" /> Delete your account?</DialogTitle>
                                <DialogDescription>This permanently erases your profile, preferences, outings history, and connection interests from our database. It cannot be undone.</DialogDescription>
                            </DialogHeader>
                            <label className="flex items-start gap-2.5 text-sm">
                                <Checkbox data-testid="delete-confirm-check" checked={delConfirm} onCheckedChange={(v) => setDelConfirm(!!v)} className="mt-0.5" />
                                <span className="text-muted-foreground">I understand this will permanently delete my account and all of my information.</span>
                            </label>
                            <div>
                                <label className="text-sm font-600">Type <span className="font-700 text-destructive">delete</span> to confirm</label>
                                <Input data-testid="delete-confirm-input" value={delWord} onChange={(e) => setDelWord(e.target.value)} placeholder="delete" className="mt-1.5" />
                            </div>
                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setDelOpen(false)} data-testid="delete-cancel">Cancel</Button>
                                <Button onClick={deleteAccount} disabled={!canDelete || deleting} data-testid="delete-account-confirm" className="rounded-full bg-destructive text-white hover:bg-destructive/90"><Trash2 className="mr-1 h-4 w-4" /> Delete account</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </TabsContent>
            </Tabs>
        </div>
    );
}
